import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import type { AuthController, AuthState } from './types.js';

const AuthContext = createContext<AuthController | null>(null);

export function AuthProvider({
  children,
  controller,
}: {
  readonly children: ReactNode;
  readonly controller: AuthController;
}) {
  return <AuthContext.Provider value={controller}>{children}</AuthContext.Provider>;
}

export function useAuthController(): AuthController {
  const controller = useContext(AuthContext);
  if (controller === null) {
    throw new Error('useAuthController must be used inside <AuthProvider>.');
  }
  return controller;
}

/** The current auth state; re-renders on login, renewal, logout, and session end. */
export function useAuth(): AuthState & { readonly controller: AuthController } {
  const controller = useAuthController();
  const state = useSyncExternalStore(
    (listener) => controller.subscribe(listener),
    () => controller.getState(),
    () => controller.getState(),
  );
  return { ...state, controller };
}

export type CallbackStatus = 'working' | 'failed';

/**
 * Completes the login redirect on the callback route (once, even under StrictMode) and hands the
 * return route to the app router. Renders the Persian progress text itself.
 */
export function AuthCallback({
  onComplete,
  onError,
  url,
}: {
  readonly onComplete: (returnTo: string) => void;
  readonly onError?: (error: unknown) => void;
  readonly url?: string;
}) {
  const controller = useAuthController();
  const started = useRef(false);
  const [status, setStatus] = useState<CallbackStatus>('working');

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    controller
      .handleCallback(url)
      .then((result) => onComplete(result.returnTo))
      .catch((error: unknown) => {
        setStatus('failed');
        onError?.(error);
      });
  }, [controller, onComplete, onError, url]);

  return (
    <p role="status" aria-live="polite">
      {status === 'working' ? 'در حال ورود…' : 'ورود انجام نشد. دوباره تلاش کنید.'}
    </p>
  );
}
